import { cx } from "@jugl-web/utils";
import React, { forwardRef, useImperativeHandle, useRef, ChangeEvent } from "react";

export interface VariableTokenInputHandle {
  insertAtCursor: (text: string) => void;
  focus: () => void;
}

type Props = {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
  inputClassName?: string;
};

export const VariableTokenInput = forwardRef<VariableTokenInputHandle, Props>(
  (
    { value, onChange, placeholder, multiline, className, inputClassName },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
      null
    );

    useImperativeHandle(
      ref,
      () => ({
        insertAtCursor: (text: string) => {
          const el = inputRef.current;
          const safeStart = el?.selectionStart ?? value.length;
          const safeEnd = el?.selectionEnd ?? value.length;
          const next = value.slice(0, safeStart) + text + value.slice(safeEnd);
          onChange(next);
          requestAnimationFrame(() => {
            const refresh = inputRef.current;
            if (!refresh) return;
            const cursor = safeStart + text.length;
            refresh.setSelectionRange(cursor, cursor);
            refresh.focus();
          });
        },
        focus: () => inputRef.current?.focus(),
      }),
      [onChange, value]
    );

    const handleChange = (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      onChange(event.target.value);
    };

    const sharedClassName = cx(
      "font-secondary text-dark text-sm leading-[140%] outline-none placeholder:text-dark-600",
      inputClassName
    );

    return (
      <div className={cx("flex w-full", className)}>
        {multiline ? (
          <textarea
            ref={(node) => {
              inputRef.current = node;
            }}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            className={cx(sharedClassName, "resize-none")}
          />
        ) : (
          <input
            ref={(node) => {
              inputRef.current = node;
            }}
            type="text"
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            className={sharedClassName}
          />
        )}
      </div>
    );
  }
);

VariableTokenInput.displayName = "VariableTokenInput";
